import { SelectionModel } from '@angular/cdk/collections';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { Case } from 'src/app/models/case';
import { Document, DocumentFilters } from 'src/app/models/document';
import { Paginator } from 'src/app/models/paginator';
import { CaseService } from 'src/app/services/case.service';
import { DocumentService } from 'src/app/services/document.service';
import { MessageService } from 'src/app/services/message.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProductServiceType } from 'src/app/models/productService';
import { DocumentClass } from 'src/app/models/documentClass';

@Component({
  selector: 'app-add-case-documents-dialog',
  templateUrl: './add-case-documents-dialog.component.html',
  styleUrls: ['./add-case-documents-dialog.component.scss']
})
export class AddCaseDocumentsDialogComponent implements OnInit {

  loading: boolean = false;
  done: boolean = false;
  noDocs: boolean = false;
  count: number = 0;

  public filters: DocumentFilters = {
    query: undefined,
    approval: [],
    classNumber: [],
    productsServices: [],
    territories: [],
    documentClass: [],
    trademark: [],
    trademarkFamily: [],
    period: {
      start: undefined,
      end: undefined
    },
    tags: []
  }

  pager: Paginator<Document> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0,
    sort: '-createdAt'
  };
  filteredDocuments: Document[] = [];

  selection = new SelectionModel<Document>(true, []);

  case?: Case;

  private readonly pagerSubject = new Subject<PageEvent>();

  restrictions: {
    selectedAndDisabled: string[],
    disabled: string[],
    tooltip?: string
  } = {
      selectedAndDisabled: [],
      disabled: [],
      tooltip: 'CASE.PROOF_ALREADY_LINKED_TO_CASE'
    };

  constructor(
    private _message: MessageService,
    @Inject(MAT_DIALOG_DATA) public data: { case: Case, niceClass?: number, productService?: string, documentClasses?: DocumentClass[] },
    private caseService: CaseService,
    private dialogRef: MatDialogRef<AddCaseDocumentsDialogComponent>,
    private documentService: DocumentService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {
    this.pagerSubject
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntilDestroyed()
      ).subscribe(async () => {
        await this.retrieveDocuments(false);
      });
  }

  async ngOnInit() {
    console.log(this.data)
    this.case = this.data.case;
    if (this.case) {
      this.filters.classNumber = this.case.niceClassification;
      this.filters.trademark = [{ ...this.case.trademark, _id: this.case.trademark.ref }];
      this.filters.period.start = moment(this.case.timePeriod.start);
      this.filters.period.end = moment(this.case.timePeriod.end);
      this.filters.nonUse = false;
      this.filters.productsServices = (this.case.productsServices && this.case.productsServices.length > 0) ? this.case.productsServices.map((ps) => ({
        _id: ps.ref,
        label: ps.label,
        type: ProductServiceType.PRODUCTS,
        classNumber: ps.classNumber,
        trademark: { ...this.case!.trademark, _id: this.case!.trademark.ref },
        translations: {
        }
      })) : [];
      this.restrictions.selectedAndDisabled = this.case.documents?.map((doc) => doc.ref) || [];
    }
    if (this.data.niceClass) {
      this.filters.classNumber = this.filters.classNumber.filter((c) => c === this.data.niceClass);
      this.filters.productsServices = this.filters.productsServices.filter((ps) => ps.classNumber === this.data.niceClass);
    }
    if (this.data.productService) {
      this.filters.productsServices = this.filters.productsServices.filter((ps) => ps._id === this.data.productService);
    }
    if (this.data.documentClasses) {
      this.filters.documentClass = this.data.documentClasses;
    }

    this.loading = true;
    this.done = false;
    await this.retrieveDocuments(true);
    this.loading = false;
    this.done = true;
  }

  onFilterChange() {
    this.resetPage();
    void this.retrieveDocuments(false);
  }

  async retrieveDocuments(init: boolean) {
    try {
      this._message.emitChange("LOADING", "START");
      const documents = await this.documentService.retrieveAll(
        this.pager.page,
        this.pager.limit,
        this.pager.offset,
        this.pager.sort,
        this.filters.territories,
        this.filters.classNumber,
        this.filters.productsServices,
        this.filters.trademark,
        this.filters.trademarkFamily,
        this.filters.documentClass,
        this.filters.period,
        this.filters.nonUse,
        this.filters.approval,
        this.filters.tags,
        this.filters.query
      )
      this.pager = {
        ...this.pager,
        ...documents
      };
      this.filteredDocuments = this.pager.docs;
      this.count = this.pager.totalDocs;
      if (init && this.pager.totalDocs === 0) {
        this.noDocs = true;
      } else if (init && this.pager.totalDocs > 0) {
        this.noDocs = false;
      }
      this._message.emitChange("LOADING", "END");
    } catch (err) {
      console.error(err);
      this._message.emitChange("LOADING", "END");
      this.toastr.error(`ERRORS.GENERIC`)
    }
  }

  resetPage() {
    this.pager.limit = 10;
    this.pager.page = 1;
    this.pager.offset = 0;
  }

  async handlePage(event: PageEvent) {
    this.pager.limit = event.pageSize;
    this.pager.page = event.pageIndex + 1;
    this.pager.offset = event.pageIndex * this.pager.limit;
    this.pagerSubject.next(event);
  }

  onSortChange(event: any) {
    if (['asc', 'desc'].includes(event.direction)) {
      this.pager.sort = `${event.direction === 'desc' ? '-' : ''}${event.active}`;
      this.retrieveDocuments(false);
    }
  }

  isDisabled() {
    return this.loading || this.selection.selected.length === 0;
  }

  onCancel() {
    this.dialogRef.close({ linked: false });
  }

  async link() {
    try {
      if (this.case?._id) {
        this.loading = true;
        await this.caseService.link(this.case._id, this.selection.selected.map((doc: Document) => doc._id!));
        this.toastr.success(this.translate.instant(this.selection.selected.length > 1 ? 'CASE.LINK_PROOFS_SUCCESS' : 'CASE.LINK_PROOF_SUCCESS', { n: this.selection.selected.length }));
        this.loading = false;
        this.dialogRef.close({ linked: true });
      }

    } catch (err) {
      console.error(err);
      this.loading = false;
      this.toastr.error(`ERRORS.GENERIC`);
    }
  }
}
